import { ErrorCodeEnum } from "api/api"
import { UserDevice } from "./device"

export interface Scope {
	id: string
	name: string
}

export interface Organization extends Scope {
	communicationPermission: "send" | "view" | "none" | null
	canEnrollPatients: boolean
	canManageNurseTimes: boolean
	hasSend?: boolean
	hasView?: boolean
	timezone?: string
	excludedFromAnalytics?: boolean
	supervisorId?: string
	supervisorName?: string
}

export interface Team extends Scope {
	id: string
	name: string
	scheduling: CRUDPermission
	canVisit: boolean
	manageContacts: CRUDPermission
}

export const ROLES = [
	"Provider",
	"Patient",
	"Provider-anon",
	"Field-support-agent",
	"Administrator",
] as const

export type Role = (typeof ROLES)[number]

export type CRUDPermission = "none" | "view" | "edit"

export interface User {
	role: Role
	fullName: string
	studyTemplate?: string
	id: string
	organizations: Organization[]
	zendeskToken: string
	gitbookToken?: string
	legal: boolean
	fieldSupportTeams?: Team[]
	isPatient?: boolean
	isProvider?: boolean
	isFieldSupportAgent?: boolean
	isProviderAnon?: boolean
	isAdmin?: boolean
	adminPermissions?: AdminPermission[]
}

export const BIOLOGICAL_SEXES = ["Male", "Female"] as const

export type BiologicalSex = (typeof BIOLOGICAL_SEXES)[number]

export const PREFERRED_GENDERS = [...BIOLOGICAL_SEXES, "Unspecified"] as const

export type PreferredGender = (typeof PREFERRED_GENDERS)[number]

export type PhoneInfo = {
	value: string
	supportsTextMessages: boolean
}

export const RELATIONSHIPS = [
	"Spouse",
	"Parent",
	"Child",
	"Sibling",
	"Guardian",
	"Caregiver",
	"Friend",
	"Other family",
] as const

export type Relationship = (typeof RELATIONSHIPS)[number] | string

export interface AlternateContact {
	_id: string
	fullName: string
	relationship: Relationship
	phoneNumber: PhoneInfo
	isActive: boolean
	createdAt?: string
	updatedAt?: string
	hasCommunications?: boolean
}

export interface AdminPermission {
	resourcePath: string[]
	actions: string[]
	internalOnly?: boolean
}

export interface AdminUser {
	id: string
	fullName: string
	displayName: string
	email: string
	emailVerified: boolean
	phoneNumber: PhoneInfo
	userType: Role
	userIdentifier: string
	excludedFromAnalytics: boolean
	inAnonymizedView: boolean
	isInternal?: boolean
	studyTemplate?: string
	hasCommunications?: boolean
	address?: string
	biologicalSex?: BiologicalSex | null
	preferredGender?: PreferredGender | null
	dateOfBirth?: string
	createdAt?: string
	updatedAt?: string
	device?: UserDevice
	careOrganizations?: Organization[]
	fieldSupportTeams?: Team[]
	alternateContacts?: AlternateContact[]
	adminPermissions?: AdminPermission[]
}

export interface PhoneCheck {
	code?: ErrorCodeEnum
	message?: string
	valid: boolean
}

export interface LegalUrls {
	privacyPolicy: string
	termsOfUse: string
}

export type AdminUserUpdatePayload = Partial<
	Pick<
		AdminUser,
		| "email"
		| "emailVerified"
		| "displayName"
		| "fullName"
		| "phoneNumber"
		| "excludedFromAnalytics"
		| "inAnonymizedView"
		| "address"
		| "dateOfBirth"
		| "biologicalSex"
		| "preferredGender"
		| "fieldSupportTeams"
		| "alternateContacts"
		| "adminPermissions"
	>
> & {
	device?: UserDevice["id"]
	patientCareOrganization?: Organization["id"] | null
	careOrganizations?: Organization["id"][]
	providerCareOrganizations?: {
		careOrganization: Organization["id"]
		communicationPermission: Organization["communicationPermission"]
		canEnrollPatients: Organization["canEnrollPatients"]
		canManageNurseTimes: Organization["canManageNurseTimes"]
	}[]
}

export type AdminUserInvitePayload = Partial<
	Pick<AdminUser, "userType" | "email" | "phoneNumber" | "adminPermissions">
>

export type MinimalUser = Pick<User, "id" | "fullName">
